// src/views/adminView/contractsView/AgregarUsuarioEmpresaModal.jsx
// Alta de un usuario de portal para una empresa EXISTENTE, desde el tab
// Cuentas. Recicla el flujo del usuario inicial de CreateContractWizard:
// checkUserExists (email/RUT) + createCompanyUser (POST users con companyId).
// El usuario queda con el perfil de administrador del portal del comercio —
// el mismo que reciben los usuarios iniciales creados por el wizard de
// contratos (KAM); este endpoint no recibe rol, lo asigna el servicio.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../../utils/toast";
import { createCompanyUser, checkUserExists } from "../../../api/user";
import { Modal } from "../../../components/ui/Modal";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";
import { FieldLabel } from "../../../components/ui/FieldLabel";
import { SearchableSelect } from "../../../components/ui/SearchableSelect";
import { RUT_STATE, rutErrorKey, validateRut } from "../../../utils/rut";
import { useCountry } from "../../../context/CountryContext";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FORM_VACIO = {
  companyId: "",
  name: "",
  lastName: "",
  email: "",
  phone: "",
  rut: "",
  password: "",
  confirmPassword: "",
};

function Field({ label, error, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel>
        {label}
        <span className="text-status-error"> *</span>
      </FieldLabel>
      {children}
      {error ? <span className="text-xs text-status-error">{error}</span> : null}
    </div>
  );
}

export default function AgregarUsuarioEmpresaModal({
  open,
  onClose,
  companies = [],
  defaultCompanyId = "",
  onDone,
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState(FORM_VACIO);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Al abrir: form limpio con la empresa del filtro activo preseleccionada.
  useEffect(() => {
    if (open) {
      setForm({ ...FORM_VACIO, companyId: defaultCompanyId || "" });
      setErrors({});
      setRutTouched(false);
    }
  }, [open, defaultCompanyId]);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: typeof e === "string" ? e : e.target.value }));

  // El RUT viaja al backend como llave de identidad (checkUserExists email/RUT): un DV
  // errado da de alta a la persona con el RUT de otro. Se valida con módulo 11 y se frena
  // el envío mientras el campo tenga contenido y no pase.
  // Fuera de Chile este campo lleva el documento local: el módulo 11 solo aplica en CL.
  const { countryCode } = useCountry();
  const esChile = countryCode === "CL";
  const rutCheck = validateRut(form.rut);
  const [rutTouched, setRutTouched] = useState(false);
  // El DV equivocado se avisa al tiro (es definitivo); "incompleto" solo al salir del
  // campo, para no marcar en rojo a quien todavía está escribiendo.
  const rutError =
    esChile &&
    (rutCheck.state === RUT_STATE.BAD_DV ||
      (rutTouched && rutCheck.state === RUT_STATE.INCOMPLETE))
      ? rutErrorKey(rutCheck)
      : null;
  // Con contenido y sin pasar módulo 11 no se envía; vacío se rige por la obligatoriedad
  // de siempre (mensaje "El RUT es requerido" al enviar).
  const rutBlocks =
    esChile && rutCheck.state !== RUT_STATE.EMPTY && rutCheck.state !== RUT_STATE.OK;
  // Un solo mensaje bajo el campo: módulo 11 si aplica, requerido solo si sigue vacío.
  const rutMessage = rutError
    ? t(rutError, { dv: rutCheck.expectedDv })
    : rutCheck.state === RUT_STATE.EMPTY
      ? errors.rut || null
      : null;
  const RUT_ERROR_ID = "agregar-usuario-rut-error";

  const submit = async () => {
    if (submitting) return;
    if (rutBlocks) return;
    const next = {};
    if (!form.companyId) next.companyId = t("Selecciona la empresa");
    if (!form.name.trim()) next.name = t("El nombre es requerido");
    if (!form.lastName.trim()) next.lastName = t("El apellido es requerido");
    const email = form.email.toLowerCase().trim();
    if (!email) next.email = t("El email es requerido");
    else if (!EMAIL_REGEX.test(email)) next.email = t("El formato del email es inválido");
    if (!form.phone.trim()) next.phone = t("El teléfono es requerido");
    if (!form.rut.trim()) next.rut = t("El RUT es requerido");
    if (!form.password.trim()) next.password = t("La contraseña es requerida");
    if (form.password !== form.confirmPassword)
      next.confirmPassword = t("Las contraseñas no coinciden");
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }

    setSubmitting(true);
    try {
      // Mismo pre-chequeo que el wizard de contratos: evita el 4xx del alta.
      const check = await checkUserExists(email, form.rut.trim());
      if (check?.error) {
        setErrors({ email: t("Error al verificar el email. Por favor, intente nuevamente.") });
        return;
      }
      if (check?.emailExists) {
        setErrors({ email: t("Este email ya está registrado en el sistema.") });
        return;
      }

      const res = await createCompanyUser({
        name: form.name.trim(),
        lastName: form.lastName.trim(),
        email,
        phone: form.phone.trim(),
        rut: form.rut.trim(),
        password: form.password,
        companyId: form.companyId,
        active: true,
        newUser: false,
        trash: false,
      });
      if (res?.error) {
        toast.error(res.error?.message || t("Error al crear el usuario"));
        return;
      }
      toast.success(t("Usuario creado exitosamente"));
      if (typeof onDone === "function") onDone();
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => !submitting && onClose()}
      size="md"
      title={t("Agregar usuario a una empresa")}
      description={t("El usuario queda como administrador del portal de ese comercio.")}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            {t("Cancelar")}
          </Button>
          <Button onClick={submit} loading={submitting} disabled={submitting || rutBlocks}>
            {t("Crear usuario")}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <Field label={t("Empresa")} error={errors.companyId}>
            <SearchableSelect
              options={companies.map((c) => ({ value: c.id, label: c.name }))}
              value={form.companyId}
              onChange={set("companyId")}
              placeholder={t("Buscar empresa…")}
              isDisabled={submitting}
            />
          </Field>
        </div>
        <Field label={t("Nombre")} error={errors.name}>
          <Input value={form.name} onChange={set("name")} disabled={submitting} />
        </Field>
        <Field label={t("Apellido")} error={errors.lastName}>
          <Input value={form.lastName} onChange={set("lastName")} disabled={submitting} />
        </Field>
        <Field label={t("Correo")} error={errors.email}>
          <Input type="email" value={form.email} onChange={set("email")} disabled={submitting} />
        </Field>
        <Field label={t("Teléfono")} error={errors.phone}>
          <Input value={form.phone} onChange={set("phone")} placeholder="+569…" disabled={submitting} />
        </Field>
        <Field label={t("RUT")}>
          <Input
            value={form.rut}
            onChange={set("rut")}
            onBlur={() => setRutTouched(true)}
            placeholder="11.111.111-1"
            disabled={submitting}
            invalid={!!rutMessage}
            aria-invalid={!!rutMessage}
            aria-describedby={rutMessage ? RUT_ERROR_ID : undefined}
          />
          {rutMessage && (
            <p id={RUT_ERROR_ID} className="text-xs text-status-error mt-1" role="alert">
              {rutMessage}
            </p>
          )}
        </Field>
        <div aria-hidden="true" className="hidden sm:block" />
        <Field label={t("Contraseña")} error={errors.password}>
          <Input
            type="password"
            autoComplete="new-password"
            value={form.password}
            onChange={set("password")}
            disabled={submitting}
          />
        </Field>
        <Field label={t("Confirmar contraseña")} error={errors.confirmPassword}>
          <Input
            type="password"
            autoComplete="new-password"
            value={form.confirmPassword}
            onChange={set("confirmPassword")}
            disabled={submitting}
          />
        </Field>
      </div>
    </Modal>
  );
}
